import React, { useState } from 'react';
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore';
import { SxStyle } from '../../../types';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import Stack from '@mui/material/Stack';
import { collection } from 'firebase/firestore';
import { useRouteMatch } from 'react-router-dom';

const getExtendedTopicInfo = (topic: string, teams: any, strings: any) => {
  if (topic.includes('TEAM_NEWS')) {
    const identifier = topic.split('_')[2];
    const team = teams.find((x: { everySportId: number }) => Number(identifier) === x.everySportId);

    const { logo, shorterName } = team;

    return {
      logo,
      shorterName,
      tooltipString: strings.fcmTopicExplorer.teamNewsFor + shorterName,
    };
  }

  if (topic.includes('TEAM_EVENTS')) {
    const identifier = topic.split('_')[2];
    const team = teams.find((x: { everySportId: number }) => Number(identifier) === x.everySportId);

    const { logo, shorterName } = team;

    return {
      logo,
      shorterName,
      tooltipString: strings.fcmTopicExplorer.teamEventsFor + shorterName,
    };
  }

  if (topic.includes('TEAM_STREAMS')) {
    const identifier = topic.split('_')[2];
    const team = teams.find((x: { abbrv: string }) => identifier === x.abbrv);

    const { logo, shorterName } = team;

    return {
      logo,
      shorterName,
      tooltipString: strings.fcmTopicExplorer.clubStreamsFor + shorterName,
    };
  }
};

const styles: SxStyle = {
  content: {
    maxWidth: '600px',
  },
  margin: (theme) => ({
    marginTop: theme.spacing(2),
  }),
  paper: {
    paddingRight: '20px',
    paddingLeft: '20px',
    paddingTop: '20px',
    paddingBottom: '20px',
  },
  resultPaper: (theme) => ({
    paddingRight: '20px',
    paddingLeft: '20px',
    paddingTop: '20px',
    paddingBottom: '20px',
    marginTop: theme.spacing(2),
  }),
  button: (theme) => ({
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.spacing(2),
  }),
  chip: (theme) => ({
    margin: theme.spacing(0.5),
  }),
};

export default function PushFcmTopicExplorer() {
  const { strings } = useStringsContext();
  const [teams, loadingTeams, errorTeams] = useCollectionDataOnce(
    collection(firebaseApp.db, `/teams`)
  );
  const [loading, setLoading] = useState(false);
  const [fcmTopicInput, setFcmTopicInput] = useState('');
  const [topicsFromFcm, setTopicsFromFcm] = useState<any>([]);
  const { params } = useRouteMatch<{ abbrv?: string }>();
  const selectedApp = params.abbrv || '';

  const onChangeFcmTopicInput = (e: any) => {
    setFcmTopicInput(e.target.value);
  };

  const onClickSearch = async () => {
    setLoading(true);
    const res = await fetchTopics(fcmTopicInput, selectedApp);
    if (res && res.length > 0) {
      setTopicsFromFcm(res);
    }

    setTopicsFromFcm([]);
    setLoading(false);
  };

  const fetchTopics = async (deviceToken: string, app: string) => {
    setLoading(true);

    const url = `${process.env.REACT_APP_POPA_BACKEND_BASE_URL}/getTopics`;

    try {
      const body = { deviceToken, appKey: app };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (response.status === 200) {
        const json = await response.json();
        const topics = Object.keys(json);
        return topics;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  if (!teams || loadingTeams || errorTeams) {
    return <></>;
  }

  return (
    <>
      <Stack sx={styles.content}>
        <Paper sx={styles.paper} elevation={2}>
          <Stack>
            <TextField
              fullWidth
              onChange={onChangeFcmTopicInput}
              sx={styles.margin}
              id="searchbox"
              label={strings.fcmTopicExplorer.enterFCMToken}
            />
            <Button
              onClick={() => onClickSearch()}
              variant="contained"
              color="primary"
              fullWidth
              sx={styles.button}
              endIcon={<SearchIcon />}
            >
              {strings.actions.search}
            </Button>
          </Stack>
        </Paper>

        {(loading || (topicsFromFcm && topicsFromFcm.length > 0)) && (
          <Paper sx={styles.resultPaper}>
            {loading && <LinearProgress />}

            {!loading &&
              topicsFromFcm &&
              topicsFromFcm.length > 0 &&
              topicsFromFcm.map((topic: any) => {
                const extendedInfo = getExtendedTopicInfo(topic, teams, strings);

                if (topic === 'WORKAROUND') {
                  return null;
                }

                return (
                  <li key={topic}>
                    <Tooltip title={extendedInfo?.tooltipString || ''}>
                      <Chip
                        avatar={
                          <Avatar alt={extendedInfo?.tooltipString} src={extendedInfo?.logo} />
                        }
                        label={topic}
                        sx={styles.chip}
                      />
                    </Tooltip>
                  </li>
                );
              })}
          </Paper>
        )}
      </Stack>
    </>
  );
}
